// 우유 매입/매출 마진 시뮬 — 매입가 고정(개당), 학교별 매출가(개당) 입력, 학생 1명당 월 1박스(24개)
import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { orgQuery, withOrg, orgDocId, stripOrgPrefix } from "@/lib/org";
import { db } from "@/lib/firebase";
import { useStudents } from "@/hooks/useStudents";

const sid = (s: string) => s.replace(/[^\w가-힣-]/g, "_");
const won = (n: number) => `${Math.round(n).toLocaleString()}원`;

export default function PricingSimulator() {
  const { students } = useStudents({ status: "active" });
  const [buyPerUnit, setBuyPerUnit] = useState(375);
  const [unitsPerBox, setUnitsPerBox] = useState(24);
  const [sellMap, setSellMap] = useState<Record<string, number>>({}); // sid(school) -> 개당 매출가
  const [editSell, setEditSell] = useState<Record<string, string>>({});

  // 전역 설정(매입가·박스) 구독
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "config", orgDocId("pricing")), (snap) => {
      if (!snap.exists()) return;
      const d: any = snap.data();
      if (typeof d.buyPerUnit === "number") setBuyPerUnit(d.buyPerUnit);
      if (typeof d.unitsPerBox === "number") setUnitsPerBox(d.unitsPerBox);
    });
    return () => unsub();
  }, []);

  // 학교별 매출가 구독
  useEffect(() => {
    const unsub = onSnapshot(orgQuery("schoolPrices"), (snap) => {
      const m: Record<string, number> = {};
      snap.docs.forEach((d) => {
        const v = (d.data() as any).sellPerUnit;
        if (typeof v === "number") m[stripOrgPrefix(d.id)] = v;
      });
      setSellMap(m);
    });
    return () => unsub();
  }, []);

  // 학교별 학생 수 (택배 포함 — 우유 매출 대상 전체)
  const schools = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of students) {
      if (!s.school) continue;
      m[s.school] = (m[s.school] || 0) + 1;
    }
    return Object.entries(m)
      .map(([school, count]) => ({ school, count }))
      .sort((a, b) => b.count - a.count);
  }, [students]);

  const sellOf = (school: string): number => {
    const id = sid(school);
    if (editSell[id] !== undefined && editSell[id] !== "") return Number(editSell[id]) || 0;
    if (editSell[id] === "") return 0;
    return sellMap[id] ?? 0;
  };

  const saveConfig = (buy: number, box: number) => {
    setDoc(
      doc(db, "config", orgDocId("pricing")),
      withOrg({ buyPerUnit: buy, unitsPerBox: box, updatedAt: serverTimestamp() }),
      { merge: true },
    );
  };
  const saveSell = (school: string, sell: number) => {
    setDoc(
      doc(db, "schoolPrices", orgDocId(sid(school))),
      withOrg({ school, sellPerUnit: sell, updatedAt: serverTimestamp() }),
      { merge: true },
    );
  };

  const rows = schools.map(({ school, count }) => {
    const sell = sellOf(school);
    const boxes = count * 1; // 학생 1명당 월 1박스
    const units = boxes * unitsPerBox;
    const revenue = units * sell;
    const cost = units * buyPerUnit;
    const margin = revenue - cost;
    return { school, count, sell, revenue, cost, margin };
  });

  const total = rows.reduce(
    (a, r) => ({
      count: a.count + r.count,
      revenue: a.revenue + r.revenue,
      cost: a.cost + r.cost,
      margin: a.margin + r.margin,
    }),
    { count: 0, revenue: 0, cost: 0, margin: 0 },
  );
  const marginRatio = total.revenue > 0 ? (total.margin / total.revenue) * 100 : 0;
  const unpriced = rows.filter((r) => r.sell <= 0).length;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">💰 단가 시뮬 (매입/매출 마진)</h1>

      {/* 전역 설정 */}
      <section className="card p-4 bg-milk-50 border-milk-200">
        <h3 className="font-bold mb-3 text-milk-800">📌 기준 설정</h3>
        <div className="flex flex-wrap items-end gap-4 text-sm">
          <label className="block">
            <div className="label">매입가 (개당)</div>
            <div className="flex items-center gap-1 mt-1">
              <input
                type="number"
                className="input w-28 text-right"
                value={buyPerUnit}
                onChange={(e) => setBuyPerUnit(Number(e.target.value))}
                onBlur={() => saveConfig(buyPerUnit, unitsPerBox)}
              />
              <span className="text-xs text-slate-500">원/개</span>
            </div>
          </label>
          <label className="block">
            <div className="label">1박스 개수</div>
            <div className="flex items-center gap-1 mt-1">
              <input
                type="number"
                className="input w-20 text-right"
                value={unitsPerBox}
                onChange={(e) => setUnitsPerBox(Number(e.target.value))}
                onBlur={() => saveConfig(buyPerUnit, unitsPerBox)}
              />
              <span className="text-xs text-slate-500">개/박스</span>
            </div>
          </label>
          <div className="text-xs text-slate-500 pb-2">
            학생 1명당 <strong>월 1박스</strong> · 박스당 매입 <strong>{won(unitsPerBox * buyPerUnit)}</strong>
          </div>
        </div>
      </section>

      {/* 합계 */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat title="학생(가구)" value={`${total.count.toLocaleString()}명`} />
        <Stat title="월 매출" value={won(total.revenue)} tone="emerald" />
        <Stat title="월 매입" value={won(total.cost)} />
        <Stat title="월 마진" value={`${won(total.margin)} (${marginRatio.toFixed(1)}%)`} tone="milk" />
      </section>

      {unpriced > 0 && (
        <div className="text-xs text-amber-700">⚠️ 매출가 미입력 {unpriced}개 학교 — 매출 0으로 계산됩니다.</div>
      )}

      {/* 학교별 테이블 */}
      <section className="card overflow-auto max-h-[60vh]">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-2">학교</th>
              <th className="p-2 text-right">학생</th>
              <th className="p-2 text-right">매출가(개당)</th>
              <th className="p-2 text-right">월 매출</th>
              <th className="p-2 text-right">월 매입</th>
              <th className="p-2 text-right">월 마진</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.school} className="border-t">
                <td className="p-2 font-medium">{r.school}</td>
                <td className="p-2 text-right">{r.count}</td>
                <td className="p-2 text-right">
                  <input
                    type="number"
                    className="input w-24 text-right py-1"
                    placeholder="0"
                    value={editSell[sid(r.school)] ?? (sellMap[sid(r.school)] ?? "")}
                    onChange={(e) => setEditSell((p) => ({ ...p, [sid(r.school)]: e.target.value }))}
                    onBlur={(e) => saveSell(r.school, Number(e.target.value) || 0)}
                  />
                </td>
                <td className="p-2 text-right text-emerald-700">{won(r.revenue)}</td>
                <td className="p-2 text-right text-slate-500">{won(r.cost)}</td>
                <td className={`p-2 text-right font-semibold ${r.margin >= 0 ? "text-milk-700" : "text-rose-600"}`}>{won(r.margin)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">학생 데이터가 없습니다.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0 bg-slate-100 font-bold">
              <tr>
                <td className="p-2">합계 ({rows.length}개 학교)</td>
                <td className="p-2 text-right">{total.count}</td>
                <td className="p-2"></td>
                <td className="p-2 text-right text-emerald-700">{won(total.revenue)}</td>
                <td className="p-2 text-right text-slate-600">{won(total.cost)}</td>
                <td className={`p-2 text-right ${total.margin >= 0 ? "text-milk-700" : "text-rose-600"}`}>{won(total.margin)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </section>

      <p className="text-xs text-slate-400">
        매출가는 입력 즉시 저장되며(학교별), 다음에 들어와도 유지됩니다. 매입가·박스 수도 저장됩니다.
      </p>
    </div>
  );
}

function Stat({ title, value, tone }: { title: string; value: string; tone?: "emerald" | "milk" }) {
  return (
    <div className="card p-3">
      <div className="label">{title}</div>
      <div className={`text-xl font-extrabold mt-1 ${tone === "emerald" ? "text-emerald-600" : tone === "milk" ? "text-milk-700" : "text-slate-700"}`}>
        {value}
      </div>
    </div>
  );
}
